import { Typography } from "@mui/material";
import { parseDataUri } from "@packages/commons";
import type jspreadsheet from "jspreadsheet";
import Papa from "papaparse";
import type React from "react";
import { useCallback, useMemo } from "react";
import { WithJSpreadSheetWrapCtx } from "../JSpreadSheetWrap";
import { JSpreadsheetDataTable } from "../JSpreadsheetDataTable";

export interface SpreadsheetPreviewProps {
  data: string; // CSV content as string or base64 data URL
  maxRows?: number;
}

export const SpreadsheetPreview: React.FC<SpreadsheetPreviewProps> = ({ data, maxRows = 100 }) => {
  const parsed = useMemo(() => {
    try {
      // Extract base64 data if it's a data URL
      let csvText = data;
      const parsed = parseDataUri(data);
      if (parsed) {
        csvText = atob(parsed.base64Data);
      }

      const result = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
      });

      const headers = result.meta.fields || [];
      const rows = (result.data as any[]).slice(0, maxRows);
      const totalRows = result.data.length;

      return { headers, rows, totalRows, error: null };
    } catch (error: any) {
      return {
        headers: [],
        rows: [],
        totalRows: 0,
        error: error.message || "Failed to parse CSV",
      };
    }
  }, [data, maxRows]);

  const getSpreadsheetOptions = useCallback((): jspreadsheet.Spreadsheet => {
    if (parsed.error || parsed.headers.length === 0) {
      return {
        worksheets: [
          {
            defaultColWidth: 100,
            columns: [],
            data: [],
          },
        ],
      };
    }

    // Convert parsed data to jspreadsheet format
    // jspreadsheet expects data as array of arrays (rows of cells, without headers)
    const jspreadsheetData: string[][] = parsed.rows.map((row: any) => {
      return parsed.headers.map((header) => String(row[header] || ""));
    });

    return {
      worksheets: [
        {
          defaultColWidth: 150,
          columns: parsed.headers.map((header) => ({
            title: header,
            type: "text" as const,
            align: "left" as const,
            width: 150,
          })),
          data: jspreadsheetData,
          allowManualInsertColumn: false,
          allowManualInsertRow: false,
          columnResize: true,
        },
      ],
    };
  }, [parsed]);

  if (parsed.error) {
    return (
      <div className="p-4">
        <Typography
          variant="body2"
          color="error"
        >
          Error parsing CSV: {parsed.error}
        </Typography>
      </div>
    );
  }

  if (parsed.headers.length === 0) {
    return (
      <div className="p-4">
        <Typography
          variant="body2"
          color="textSecondary"
        >
          No data to display
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {parsed.totalRows > maxRows && (
        <div className="px-4 py-2 border-b border-border">
          <Typography
            variant="caption"
            color="textSecondary"
          >
            Showing first {maxRows} of {parsed.totalRows} rows
          </Typography>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <WithJSpreadSheetWrapCtx>
          <JSpreadsheetDataTable
            fitToContainer={false}
            getSpreadsheetOptions={getSpreadsheetOptions}
            className="h-full"
          />
        </WithJSpreadSheetWrapCtx>
      </div>
    </div>
  );
};
